import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PreAlert } from '../../../../models/pre-alert.models';

export interface PreAlertInputData {
  ladingBill: string;
  departureLocation: string;
  arrivalLocation: string;
  etd: string;
  eta: string;
  weight: number;
  chargeableWeight: number;
  volumetricWeight: number;
  pieces?: number;
  transportCompany?: string;
  vesselNumber?: string;
}

export interface PreAlertModalResult {
  action: 'save' | 'cancel';
  data?: PreAlertInputData;
}

// Custom Validators
export class PreAlertValidators {
  static dateNotInPast(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    
    const inputDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (inputDate < today) {
      return { dateInPast: { actual: inputDate, today } };
    }
    
    return null;
  }

  static etaAfterEtd(form: AbstractControl): ValidationErrors | null {
    if (!form.get) return null;
    
    const etd = form.get('etd')?.value;
    const eta = form.get('eta')?.value;
    
    if (!etd || !eta) return null;
    
    const etdDate = new Date(etd);
    const etaDate = new Date(eta);
    
    if (etaDate <= etdDate) {
      return { etaNotAfterEtd: { etd: etdDate, eta: etaDate } };
    }
    
    return null;
  }
}

@Component({
  selector: 'app-pre-alert-input-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './pre-alert-input-modal.component.html',
  styleUrl: './pre-alert-input-modal.component.css'
})
export class PreAlertInputModalComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();

  @Input() existingData: Partial<PreAlert> | null = null;
  @Input() isVisible: boolean = false;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() title: string = 'Pre-Alert Information';
  @Input() isLoading: boolean = false;
  @Input() computedPieces: number | null = null;
  @Input() filename: string | null = null;
  
  @Output() modalResult = new EventEmitter<PreAlertModalResult>();
  @Output() visibilityChange = new EventEmitter<boolean>();

  preAlertForm: FormGroup;
  error: string | null = null;
  validationMessages: { [key: string]: string } = {};

  // Computed pieces state
  isPiecesComputed = false;
  computedPiecesText = '';
  
  // Auto-populated filename state
  isLadingBillAutoPopulated = false;
  autoPopulatedFilename = '';

  // Auto-fill options
  autoFillWeights = true;
  useVolumetricMultiplier = true;
  volumetricMultiplier = 167; // Standard air cargo volumetric divisor (kg/m³)

  constructor(
    private fb: FormBuilder
  ) {
    this.preAlertForm = this.createForm();
  }

  ngOnInit(): void {
    // Watch for weight changes to auto-fill related fields
    this.preAlertForm.get('weight')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(weight => {
        if (this.autoFillWeights && weight && weight > 0) {
          this.autoFillWeightFields(weight);
        }
      });

    // Watch for manual changes to lading bill field
    this.preAlertForm.get('ladingBill')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        // If user manually edits the field, clear the auto-populated indicator
        if (this.isLadingBillAutoPopulated && value !== this.cleanFilename(this.filename || '')) {
          this.isLadingBillAutoPopulated = false;
          this.autoPopulatedFilename = '';
        }
      });

    // Watch for form validation changes
    this.preAlertForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateValidationMessages();
      });

    // Initialize with existing data if provided
    if (this.existingData) {
      this.populateForm(this.existingData);
    }
    
    // Handle computed pieces if available
    this.handleComputedPiecesChange();
    
    // Handle filename if available
    this.handleFilenameChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['computedPieces'] && this.preAlertForm) {
      this.handleComputedPiecesChange();
    }
    
    if (changes['filename'] && this.preAlertForm) {
      this.handleFilenameChange();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      ladingBill: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
        Validators.pattern(/^[A-Z0-9\-_]+$/i)
      ]],
      departureLocation: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      arrivalLocation: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      etd: ['', [
        Validators.required
      ]],
      eta: ['', [
        Validators.required
      ]],
      weight: ['', [
        Validators.required,
        Validators.min(0.1),
        Validators.max(50000),
        Validators.pattern(/^\d+(\.\d{1,3})?$/)
      ]],
      chargeableWeight: ['', [
        Validators.min(0.1),
        Validators.max(50000),
        Validators.pattern(/^\d+(\.\d{1,3})?$/)
      ]],
      volumetricWeight: ['', [
        Validators.min(0.1),
        Validators.max(50000),
        Validators.pattern(/^\d+(\.\d{1,3})?$/)
      ]],
      pieces: ['', [
        Validators.min(1),
        Validators.max(10000),
        Validators.pattern(/^\d+$/)
      ]],
      transportCompany: ['', [
        Validators.maxLength(100)
      ]],
      vesselNumber: ['', [
        Validators.maxLength(50),
        Validators.pattern(/^[A-Z0-9\-_\s]*$/i)
      ]]
    }, { validators: [PreAlertValidators.etaAfterEtd] });
  }

  private populateForm(data: Partial<PreAlert>): void {
    const formData = {
      ladingBill: data.ladingBill || '',
      departureLocation: data.departureLocation || '',
      arrivalLocation: data.arrivalLocation || '',
      etd: data.etd ? this.formatDateForInput(data.etd) : '',
      eta: data.eta ? this.formatDateForInput(data.eta) : '',
      weight: data.weight || '',
      chargeableWeight: data.chargeableWeight || '',
      volumetricWeight: data.volumetricWeight || '',
      pieces: data.pieces || '',
      transportCompany: data.transportCompany || '',
      vesselNumber: data.vesselNumber || ''
    };

    this.preAlertForm.patchValue(formData);
    
    // If existing data has a lading bill, disable auto-population
    if (data.ladingBill) {
      this.isLadingBillAutoPopulated = false;
      this.autoPopulatedFilename = '';
    }
    
    // Re-handle computed pieces after form is populated to ensure proper state
    this.handleComputedPiecesChange();
  }

  private formatDateForInput(date: Date | string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private autoFillWeightFields(weight: number): void {
    if (!this.autoFillWeights) return;

    // Auto-fill chargeable weight (copy from actual weight as starting point)
    if (!this.preAlertForm.get('chargeableWeight')?.value) {
      this.preAlertForm.get('chargeableWeight')?.setValue(weight, { emitEvent: false });
    }

    // Auto-fill volumetric weight (using standard calculation as starting point)
    if (!this.preAlertForm.get('volumetricWeight')?.value && this.useVolumetricMultiplier) {
      // This is just a starting estimate - user should adjust based on actual dimensions
      const estimatedVolumetric = weight * 1.2; // Rough estimate
      this.preAlertForm.get('volumetricWeight')?.setValue(
        Math.round(estimatedVolumetric * 100) / 100, 
        { emitEvent: false }
      );
    }
  }

  private handleComputedPiecesChange(): void {
    const piecesControl = this.preAlertForm.get('pieces');
    if (!piecesControl) return;

    if (this.computedPieces && this.computedPieces > 0) {
      // Enable computed pieces mode
      this.isPiecesComputed = true;
      this.computedPiecesText = `${this.computedPieces} unique carton${this.computedPieces > 1 ? 's' : ''} found`;
      
      // Set the computed value and disable the field
      piecesControl.setValue(this.computedPieces, { emitEvent: false });
      piecesControl.disable({ emitEvent: false });
    } else {
      // Disable computed pieces mode
      this.isPiecesComputed = false;
      this.computedPiecesText = '';
      
      // Re-enable the field for manual input
      piecesControl.enable({ emitEvent: false });
    }
  }

  private handleFilenameChange(): void {
    const ladingBillControl = this.preAlertForm.get('ladingBill');
    if (!ladingBillControl) return;

    // Only auto-populate if the field is empty and we have a filename
    if (this.filename && (!ladingBillControl.value || ladingBillControl.value.trim() === '')) {
      const cleanedFilename = this.cleanFilename(this.filename);
      if (cleanedFilename) {
        ladingBillControl.setValue(cleanedFilename, { emitEvent: false });
        this.isLadingBillAutoPopulated = true;
        this.autoPopulatedFilename = this.filename;
      }
    } else {
      // Reset auto-populated state if filename changes but field already has value
      this.isLadingBillAutoPopulated = false;
      this.autoPopulatedFilename = '';
    }
  }

  private cleanFilename(filename: string): string {
    if (!filename) return '';

    // Remove file extension (common Excel formats)
    const cleanName = filename
      .replace(/\.(xlsx|xls|csv|xlsm|xlsb)$/i, '')
      .trim();

    // Replace underscores and multiple spaces with single hyphens
    const normalized = cleanName
      .replace(/[_\s]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

    // Ensure it matches the lading bill pattern (alphanumeric, hyphens, underscores)
    const sanitized = normalized.replace(/[^A-Z0-9\-_]/gi, '');

    // Limit length to fit within validation constraints
    return sanitized.substring(0, 50);
  }

  private updateValidationMessages(): void {
    this.validationMessages = {};
    
    Object.keys(this.preAlertForm.controls).forEach(field => {
      const control = this.preAlertForm.get(field);
      if (control && control.invalid && (control.dirty || control.touched)) {
        this.validationMessages[field] = this.getFieldErrorMessage(field, control.errors);
      }
    });

    // Check form-level validation errors
    if (this.preAlertForm.errors) {
      if (this.preAlertForm.errors['etaNotAfterEtd']) {
        this.validationMessages['eta'] = 'ETA must be after ETD';
      }
    }
  }

  private getFieldErrorMessage(fieldName: string, errors: ValidationErrors | null): string {
    if (!errors) return '';

    const fieldMessages: { [key: string]: { [errorType: string]: string } } = {
      ladingBill: {
        required: 'Bill of Lading is required',
        minlength: 'Bill of Lading must be at least 3 characters',
        maxlength: 'Bill of Lading cannot exceed 50 characters',
        pattern: 'Bill of Lading can only contain letters, numbers, hyphens, and underscores'
      },
      departureLocation: {
        required: 'Departure location is required',
        minlength: 'Departure location must be at least 2 characters',
        maxlength: 'Departure location cannot exceed 100 characters'
      },
      arrivalLocation: {
        required: 'Arrival location is required',
        minlength: 'Arrival location must be at least 2 characters',
        maxlength: 'Arrival location cannot exceed 100 characters'
      },
      etd: {
        required: 'Estimated Time of Departure is required',
        dateInPast: 'ETD cannot be in the past'
      },
      eta: {
        required: 'Estimated Time of Arrival is required',
        dateInPast: 'ETA cannot be in the past'
      },
      weight: {
        required: 'Weight is required',
        min: 'Weight must be at least 0.1 kg',
        max: 'Weight cannot exceed 50,000 kg',
        pattern: 'Weight must be a valid number with up to 3 decimal places'
      },
      chargeableWeight: {
        min: 'Chargeable weight must be at least 0.1 kg',
        max: 'Chargeable weight cannot exceed 50,000 kg',
        pattern: 'Chargeable weight must be a valid number with up to 3 decimal places'
      },
      volumetricWeight: {
        min: 'Volumetric weight must be at least 0.1 kg',
        max: 'Volumetric weight cannot exceed 50,000 kg',
        pattern: 'Volumetric weight must be a valid number with up to 3 decimal places'
      },
      pieces: {
        min: 'Pieces must be at least 1',
        max: 'Pieces cannot exceed 10,000',
        pattern: 'Pieces must be a whole number'
      },
      transportCompany: {
        maxlength: 'Transport company name cannot exceed 100 characters'
      },
      vesselNumber: {
        maxlength: 'Vessel number cannot exceed 50 characters',
        pattern: 'Vessel number can only contain letters, numbers, hyphens, underscores, and spaces'
      }
    };

    const fieldMessageMap = fieldMessages[fieldName];
    if (!fieldMessageMap) return 'Invalid value';

    // Get the first error type and return corresponding message
    const errorType = Object.keys(errors)[0];
    return fieldMessageMap[errorType] || 'Invalid value';
  }

  onSave(): void {
    if (this.preAlertForm.invalid) {
      this.markFormGroupTouched(this.preAlertForm);
      this.updateValidationMessages();
      this.error = 'Please correct the errors above before saving.';
      return;
    }

    this.error = null;
    const formValue = this.preAlertForm.value;
    
    // Get pieces value, including from disabled controls (computed pieces)
    const piecesControl = this.preAlertForm.get('pieces');
    const piecesValue = piecesControl?.disabled ? piecesControl.value : formValue.pieces;
    
    // Convert form data to the expected format
    const preAlertData: PreAlertInputData = {
      ladingBill: formValue.ladingBill.trim(),
      departureLocation: formValue.departureLocation.trim(),
      arrivalLocation: formValue.arrivalLocation.trim(),
      etd: formValue.etd,
      eta: formValue.eta,
      weight: parseFloat(formValue.weight),
      chargeableWeight: formValue.chargeableWeight ? parseFloat(formValue.chargeableWeight) : parseFloat(formValue.weight),
      volumetricWeight: formValue.volumetricWeight ? parseFloat(formValue.volumetricWeight) : parseFloat(formValue.weight),
      pieces: piecesValue ? parseInt(piecesValue, 10) : undefined,
      transportCompany: formValue.transportCompany?.trim() || undefined,
      vesselNumber: formValue.vesselNumber?.trim() || undefined
    };

    this.modalResult.emit({
      action: 'save',
      data: preAlertData
    });
  }

  onCancel(): void {
    this.modalResult.emit({
      action: 'cancel'
    });
  }

  closeModal(): void {
    this.onCancel();
  }

  onBackdropMouseDown(event: MouseEvent): void {
    // Only close if the mousedown happened directly on the backdrop (not on modal content)
    const target = event.target as HTMLElement;
    if (target.classList.contains('modal') || target.classList.contains('pre-alert-modal')) {
      this.closeModal();
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control) {
        control.markAsTouched();
        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      }
    });
  }

  // Helper methods for template
  hasFieldError(fieldName: string): boolean {
    const control = this.preAlertForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getFieldError(fieldName: string): string {
    return this.validationMessages[fieldName] || '';
  }

  isFieldRequired(fieldName: string): boolean {
    const control = this.preAlertForm.get(fieldName);
    return control?.hasError('required') || false;
  }

  toggleAutoFillWeights(): void {
    this.autoFillWeights = !this.autoFillWeights;
  }

  resetWeightFields(): void {
    this.preAlertForm.get('chargeableWeight')?.setValue('');
    this.preAlertForm.get('volumetricWeight')?.setValue('');
  }

  // Getters for template
  get modalTitle(): string {
    return this.mode === 'edit' ? `Edit ${this.title}` : `Create ${this.title}`;
  }

  get saveButtonText(): string {
    return this.mode === 'edit' ? 'Update Pre-Alert' : 'Save Pre-Alert';
  }

  get isFormValid(): boolean {
    return this.preAlertForm.valid;
  }

  get hasFormErrors(): boolean {
    return Object.keys(this.validationMessages).length > 0;
  }

  getErrorCount(): number {
    return Object.keys(this.validationMessages).length;
  }

  // Method to clear auto-populated filename (for manual reset)
  clearAutoPopulatedFilename(): void {
    this.isLadingBillAutoPopulated = false;
    this.autoPopulatedFilename = '';
  }

  // Method to restore filename auto-population
  restoreFilenameAutoPopulation(): void {
    if (this.filename) {
      const ladingBillControl = this.preAlertForm.get('ladingBill');
      if (ladingBillControl) {
        const cleanedFilename = this.cleanFilename(this.filename);
        if (cleanedFilename) {
          ladingBillControl.setValue(cleanedFilename, { emitEvent: false });
          this.isLadingBillAutoPopulated = true;
          this.autoPopulatedFilename = this.filename;
        }
      }
    }
  }
}